import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { PageHero } from "@/components/PublicLayout";
import { TextField, TextAreaField, SelectField } from "@/components/FormKit";
import { SuccessScreen } from "@/pages/BookRide";
import { PlayButton, VideoModal } from "@/components/VideoModal";
import { useSite, mediaUrl } from "@/context/SiteContext";
import { ODP_SERVICES, ODP_SERVICE_OPTIONS, COUNTIES, COMPANY, IMAGES } from "@/lib/data";

const empty = {
  individual_name: "", contact_name: "", relationship: "", phone: "", email: "",
  county: "", supports_coordinator: "", sco: "", requested_service: "",
  preferred_start_date: "", general_notes: "",
};

function ReferralForm({ innerRef }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.individual_name || !form.contact_name || !form.phone || !form.requested_service) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/odp-referrals", form);
      setDone(true);
      toast.success("ODP referral submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <section ref={innerRef} className="scroll-mt-24">
        <SuccessScreen title="ODP Referral Received"
          message="Thank you. A CareDeserved team member will reach out to discuss service needs, availability, and next steps. Submitting a referral does not guarantee acceptance, authorization, funding, eligibility, or immediate service availability." />
      </section>
    );
  }

  return (
    <section ref={innerRef} id="referral-form" className="scroll-mt-24 bg-brand-bg py-16 sm:py-24" data-testid="odp-referral-section">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <p className="eyebrow text-brand-purple">ODP Referral</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Submit an ODP Referral</h2>
          <p className="mt-2 text-slate-600">
            For individuals, families, Supports Coordinators, Supports Coordination Organizations, Administrative Entities, and provider partners.
          </p>
        </div>
        <form onSubmit={submit} data-testid="odp-referral-form" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Individual Name" required testid="odp-individual" wrapClass="sm:col-span-2" value={form.individual_name} onChange={setE("individual_name")} />
            <TextField label="Contact / Representative Name" required testid="odp-contact" value={form.contact_name} onChange={setE("contact_name")} />
            <TextField label="Relationship to Individual" testid="odp-relationship" value={form.relationship} onChange={setE("relationship")} placeholder="e.g. Parent, Guardian, Supports Coordinator" />
            <TextField label="Phone" required testid="odp-phone" type="tel" value={form.phone} onChange={setE("phone")} />
            <TextField label="Email" testid="odp-email" type="email" value={form.email} onChange={setE("email")} />
            <SelectField label="County" testid="odp-county" value={form.county} onValueChange={set("county")}
              placeholder="Select county" options={[...COUNTIES.map((c) => `${c} County`), "Other"]} />
            <SelectField label="Requested Service" required testid="odp-service" value={form.requested_service} onValueChange={set("requested_service")}
              placeholder="Select a service" options={ODP_SERVICE_OPTIONS} />
            <TextField label="Supports Coordinator" testid="odp-sc" value={form.supports_coordinator} onChange={setE("supports_coordinator")} />
            <TextField label="Supports Coordination Organization" testid="odp-sco" value={form.sco} onChange={setE("sco")} />
            <TextField label="Preferred Start Date" testid="odp-start" type="date" value={form.preferred_start_date} onChange={setE("preferred_start_date")} />
            <TextAreaField label="General Notes" testid="odp-notes" wrapClass="sm:col-span-2" value={form.general_notes} onChange={setE("general_notes")}
              placeholder="Service needs, schedule preferences, or anything helpful for our team." />
          </div>

          <div className="mt-6 flex items-start gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="odp-disclaimer">
            <Icon name="ShieldCheck" size={20} weight="fill" className="mt-0.5 shrink-0" />
            Submitting a referral does not guarantee acceptance, authorization, funding, eligibility, or immediate service availability. Please do not include diagnoses, medical records, Social Security numbers, or Medicaid numbers in this form.
          </div>

          <Button type="submit" disabled={loading} data-testid="odp-submit"
            className="mt-6 h-14 w-full rounded-full bg-brand-purple text-base font-semibold text-white hover:bg-brand-purple2">
            {loading ? "Submitting…" : "Submit ODP Referral"}
          </Button>
          <p className="mt-4 text-center text-sm text-slate-500">
            Questions? Call <a href={COMPANY.phoneHref} className="font-semibold text-brand-blue">{COMPANY.phone}</a>
          </p>
        </form>
      </div>
    </section>
  );
}

export default function OdpServices() {
  const formRef = useRef(null);
  const content = useSite();
  const [video, setVideo] = useState(false);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });
  const pic = (id, fb) => (id ? mediaUrl(id) : fb);
  const odpImg = {
    "Home & Community Habilitation": pic(content.odp_hab_image_id, IMAGES.odpHab),
    "Companion Services": pic(content.odp_companion_image_id, IMAGES.odpCompanion),
    "Registered Nursing": pic(content.odp_nursing_image_id, IMAGES.odpNursing),
    "Non-Emergency Transportation": pic(content.wheelchair_image_id, IMAGES.wheelchair),
  };
  const videoThumb = pic(content.odp_video_image_id, IMAGES.odpCommunity);

  return (
    <>
      <PageHero eyebrow="Pennsylvania ODP Services"
        title="ODP Services That Support Independence, Community & Quality of Life"
        subtitle="CareDeserved LLC is a qualified Pennsylvania ODP provider offering Home & Community Habilitation, Companion Services, Registered Nursing, and Transportation. Our team focuses on dignity, independence, safety, community participation, and person-centered support.">
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button data-testid="odp-hero-request" onClick={scrollToForm}
            className="h-14 rounded-full bg-brand-purple px-8 text-base font-semibold text-white hover:bg-brand-purple2">
            Request ODP Services
          </Button>
          <Button data-testid="odp-hero-refer" onClick={scrollToForm} variant="outline"
            className="h-14 rounded-full border-2 border-white/50 bg-white/5 px-8 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/15">
            Make an ODP Referral
          </Button>
        </div>
        <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-teal/20 px-4 py-2 text-sm font-semibold text-brand-teal2" data-testid="odp-statewide-note">
          <Icon name="MapPinLine" size={18} weight="fill" /> Statewide ODP referrals welcome — contact us to confirm availability
        </p>
      </PageHero>

      {/* NOW ACCEPTING banner */}
      <section className="bg-brand-teal" data-testid="odp-referral-banner">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-center sm:flex-row sm:text-left sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle" size={24} weight="fill" className="shrink-0 text-white" />
            <p className="font-heading text-lg font-bold uppercase tracking-wide text-white sm:text-xl">Now Accepting ODP Referrals</p>
          </div>
          <Button data-testid="odp-banner-refer" onClick={scrollToForm}
            className="h-11 rounded-full bg-white px-6 font-semibold text-brand-teal hover:bg-white/90">
            Submit an ODP Referral
          </Button>
        </div>
      </section>

      {/* See Care Beyond the Ride — video storytelling */}
      <VideoModal open={video} onOpenChange={setVideo} videoUrl={content.odp_video_url} />
      <section className="bg-white py-16 sm:py-24" data-testid="odp-video-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:px-8 lg:grid-cols-2">
          <div>
            <p className="eyebrow text-brand-purple">Care Beyond the Ride</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Supporting Independence, Community &amp; Quality of Life
            </h2>
            <p className="mt-5 text-slate-600 md:text-lg">
              Our ODP services help individuals build everyday skills, participate in their communities, and receive
              compassionate support at home — with dignity and independence at the center of everything we do.
            </p>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {["Person-centered support", "Community participation", "Skill-building & independence", "Compassionate professional staff"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Icon name="CheckCircle" size={20} weight="fill" className="mt-0.5 shrink-0 text-brand-teal" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <button type="button" onClick={() => setVideo(true)} data-testid="odp-video-play"
            className="group relative block aspect-video w-full overflow-hidden rounded-3xl shadow-xl focus:outline-none">
            <img src={videoThumb} alt="Individuals participating in community activities with CareDeserved support"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-brand-blue/25 transition-colors group-hover:bg-brand-blue/35" />
            <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-teal shadow-2xl ring-4 ring-white/40 transition-transform duration-300 group-hover:scale-110">
              <Icon name="Play" weight="fill" size={30} className="ml-1" />
            </span>
            <span className="absolute bottom-4 left-4 rounded-full bg-brand-blue/70 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm">See Care Beyond the Ride</span>
          </button>
        </div>
      </section>

      {/* Service cards */}
      <section className="bg-brand-purple/[0.04] py-16 sm:py-24" data-testid="odp-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow text-brand-purple">Qualified ODP Services</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Person-Centered Support at Home &amp; in the Community
            </h2>
          </div>
          <div className="mt-12 grid gap-7 sm:grid-cols-2">
            {ODP_SERVICES.map((s) => (
              <div key={s.title} data-testid={`odp-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
                <div className="relative h-56 overflow-hidden">
                  <img src={odpImg[s.title] || IMAGES.odpCommunity} alt={s.title} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute -bottom-5 left-6 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-purple text-white shadow-lg">
                    <Icon name={s.icon} size={24} weight="fill" />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7 pt-8">
                  <h3 className="font-heading text-xl font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supports Coordinators callout */}
      <section className="bg-brand-blue noise-overlay py-16 sm:py-20" data-testid="odp-sc-section">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span className="inline-block rounded-full bg-brand-teal px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">Pennsylvania ODP Services</span>
          <h2 className="mt-5 font-heading text-2xl font-bold text-white sm:text-3xl">Looking for Services for an Individual You Support?</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-300 md:text-lg">
            CareDeserved is a qualified Pennsylvania ODP provider offering Home &amp; Community Habilitation, Companion
            Services, Registered Nursing, and Transportation. We welcome ODP referrals from individuals, families,
            Supports Coordinators, SCOs, Administrative Entities, and community partners across Pennsylvania, subject to
            service availability and individual needs.
          </p>
          <Button data-testid="odp-sc-refer" onClick={scrollToForm}
            className="mt-8 h-13 rounded-full bg-brand-purple px-8 py-3 font-semibold text-white hover:bg-brand-purple2">
            Make an ODP Referral
          </Button>
        </div>
      </section>

      {/* Who Can Refer? */}
      <section className="bg-white py-16 sm:py-24" data-testid="odp-who-section">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="eyebrow text-brand-purple">Referral Partners</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Who Can Refer?</h2>
          <p className="mt-4 text-slate-600 md:text-lg">CareDeserved welcomes inquiries and referrals from:</p>
          <div className="mx-auto mt-8 grid max-w-3xl gap-4 sm:grid-cols-2">
            {[
              "Individuals and families",
              "Supports Coordinators",
              "Supports Coordination Organizations",
              "Administrative Entities",
              "Provider agencies and community partners",
            ].map((who) => (
              <div key={who} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-brand-bg p-4 text-left">
                <Icon name="CheckCircle" size={22} weight="fill" className="shrink-0 text-brand-teal" />
                <span className="text-[15px] font-medium text-slate-800">{who}</span>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-sm text-slate-500">Referrals welcomed across Pennsylvania, subject to service availability and individual needs.</p>
          <Button data-testid="odp-who-refer" onClick={scrollToForm}
            className="mt-8 h-13 rounded-full bg-brand-purple px-8 py-3 font-semibold text-white hover:bg-brand-purple2">
            Make an ODP Referral
          </Button>
        </div>
      </section>

      <ReferralForm innerRef={formRef} />
    </>
  );
}
